export type AmountMode = "KEEP" | "REMOVE";
export type ManualPayDecision = boolean;
/**
 * Local-only collapsed action (APPLY | DELETE) derived from MatchAction for
 * UI helpers. Not sent on the agent wire — the API maps `matchAction` itself.
 */
export type MatchingAction = "APPLY" | "DELETE";
/**
 * CSV / wire MatchAction verb (R-APP-13, Product-PRDs #406).
 * APPLY writes TargetAmount with the override on.
 * REAPPLY turns the override back on using the retained amount (TargetAmount blank).
 * DELETE_KEEP_AMOUNT turns the override off and keeps the stored amount.
 * DELETE turns the override off and clears the amount.
 */
export type MatchingCsvAction =
  | "APPLY"
  | "REAPPLY"
  | "DELETE"
  | "DELETE_KEEP_AMOUNT";

/** R-PER-2 — PRD #379 pay-run lifecycle states. */
export type PayRunState =
  | "Pending"
  | "In Progress"
  | "Requires Authorisation"
  | "Authorised"
  | "Finalised"
  | "Closed";

export const PAY_RUN_WRITABLE_STATES: ReadonlySet<PayRunState> = new Set([
  "Pending",
  "In Progress",
]);

export function isPayRunWritable(state: PayRunState): boolean {
  return PAY_RUN_WRITABLE_STATES.has(state);
}

export interface PayGroupOption {
  payGroupId: string;
  payGroupName: string;
}

export interface BoundPayRun {
  payRunId: string;
  payGroupId: string;
  payGroupName: string;
  periodEnd: string;
  state: PayRunState;
}

export interface MatchingRow {
  /** 1-based row number in the source file (header = 1). */
  fileRow: number;
  /** Bound at upload from portal pay-run context (not a CSV column — R-PER-1). */
  payRunId: string;
  matchAction: MatchingCsvAction;
  /**
   * Raw MatchAction cell from the CSV (may be invalid, e.g. CLEAR).
   * Used for table display so parse stubs don't pretend to be APPLY.
   */
  rawMatchAction: string;
  employeeCode: string;
  componentCode: string | null;
  componentName: string;
  startDate: string | null;
  finishDate: string | null;
  /** Parsed amount, or null when blank (DELETE / omit). */
  targetAmount: number | null;
  /** Raw amount cell for issues CSV fidelity. */
  rawAmount: string;
  /**
   * Local-only engine-path hints derived from MatchAction via
   * `deriveRowDecisions` (row-edit / bulk actions). Not part of the agent wire.
   */
  manualPay: ManualPayDecision;
  amountMode: AmountMode;
  /** Local-only collapsed action (APPLY | DELETE). Not part of the agent wire. */
  action: MatchingAction;
  /** Unknown source columns preserved for issues CSV. */
  extraColumns: Record<string, string>;
}

export interface MatchingParseError {
  fileRow: number | null;
  message: string;
}

/**
 * `ok: true` — every data row parsed.
 * `ok: false` with `rows` — headers OK but one or more rows failed; `rows`
 *   includes best-effort stubs for failed lines so the report table can still
 *   show the CSV. File-level failures omit `rows`.
 */
export type MatchingParseOutcome =
  | { ok: true; rows: MatchingRow[] }
  | { ok: false; errors: MatchingParseError[]; rows?: MatchingRow[] };

export interface MatchingImportError {
  index: number | null;
  employeeCode: string | null;
  field: string | null;
  error: string;
}

export interface MatchingImportWarning {
  code: string;
  employeeCode: string | null;
  indices: number[];
  warning: string;
}

export interface ResolvedPacketRow {
  index: number;
  employeeCode: string;
  componentName: string;
  /**
   * Not returned by Christmas `component-matches/values` — real resolve leaves
   * this null. Mock / local fixtures may still populate it for demos.
   */
  payPacketId: string | null;
  /**
   * Not returned by Christmas values — real resolve leaves this null.
   * CSV `componentCode` on the input row is separate.
   */
  componentCode?: string | null;
  currentAmount: number | null;
  manualPayActive: boolean;
  /**
   * Amount stored on the line while the override is off (R-APP-13a).
   * Derived from Christmas `manualAmount` when `manualFlag` is false.
   */
  retainedAmount: number | null;
  /**
   * Pacific default / mock-only — Christmas has no equivalent field.
   * Optional so real resolve can omit it (defaults to supported).
   */
  supportsManualPay?: boolean;
  companyComponentId: string | null;
  status: "ok" | "not_found" | "unsupported" | "ambiguous";
}

export interface MatchingImportWorkflowResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: MatchingImportError[];
  warnings?: MatchingImportWarning[];
  resolvedRows?: ResolvedPacketRow[];
}
